import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  forwardRef,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { IMaskModule } from 'angular-imask';

import IMask from 'imask';

@Component({
  selector: 'rx-input-datepicker',
  templateUrl: './rx-input-datepicker.component.html',
  styleUrls: ['./rx-input-datepicker.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, IMaskModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RxInputDatepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RxInputDatepickerComponent),
      multi: true,
    },
  ],
})
export class RxInputDatepickerComponent
  implements OnInit, ControlValueAccessor, OnChanges, Validator
{
  @Input()
  public placeholder = 'Choose a date';

  @Input()
  public keepPlaceholder = false;

  @Input()
  public maxDate: Date | any = new Date();

  @Input()
  public minDate: Date | any = new Date('1900-01-01');

  @Input()
  public isEUFormat = false;

  @Input()
  public set value(date: string) {
    this._value = date;
  }
  public get value() {
    return this._value;
  }

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter();

  public control: FormControl | any;
  public date: string | any;
  public mask: any | any;
  public isRequired = false;
  public isRequiredChecked = false;

  public onChange = (value: string | any) => {};
  public onTouched = () => {};

  private _value: string;
  private _euFormat = 'd{/}`m{/}`Y';
  private _usFormat = 'm{/}`d{/}`Y';

  constructor() {}

  public ngOnInit() {}

  public ngOnChanges(changes: SimpleChanges | any): void {
    if (changes.isEUFormat) {
      this.initMask();
    }

    this.onChange(this.value);
  }

  private initMask() {
    this.mask = {
      mask: Date,
      pattern: this.isEUFormat ? this._euFormat : this._usFormat,
      overwrite: true,
      lazy: false,
      autofix: false,
      blocks: {
        d: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
          placeholderChar: 'd',
        },
        m: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
          placeholderChar: 'm',
        },
        Y: {
          mask: IMask.MaskedRange,
          from: this.minDate.getFullYear(),
          to: 9999,
          placeholderChar: 'y',
        },
      },
      // define date -> str convertion
      format: (date) => {
        let day = date.getDate();
        let month = date.getMonth() + 1;
        const year = date.getFullYear();

        if (day < 10) day = '0' + day;
        if (month < 10) month = '0' + month;

        if (this.isEUFormat) {
          return [day, month, year].join('/');
        }

        return [month, day, year].join('/');
      },
      // define str -> date convertion
      parse: (str) => {
        const yearMonthDay = str.split('/');

        if (this.isEUFormat) {
          return new Date(yearMonthDay[2], yearMonthDay[1] - 1, yearMonthDay[0]);
        }

        return new Date(yearMonthDay[2], yearMonthDay[0] - 1, yearMonthDay[1]);
      },
    };
  }

  // Register for change
  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  // Register for touch/click
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Update the model and changes
  public writeValue(value: string): void {
    if (this._value !== value) {
      this._value = value;
      this.setDate(value);
      this.onChange(this._value);
    }
  }

  public isValid(): boolean {
    return this.control && !this.control.pending && !this.control.errors && !this.control.invalid;
  }

  // validate
  public validate(control: FormControl): ValidationErrors | any {
    this.control = control;

    // TODO
    if (!this.isRequiredChecked) {
      this.isRequiredChecked = true;
      this.isRequired = this.control.errors && this.control.errors['required'];
    }

    return control.valid ? null : control.errors;
  }

  public valueChanged() {
    const yearMonthDay = this.date.split('/');

    if (this.isEUFormat) {
      this._value = `${yearMonthDay[2]}-${yearMonthDay[1]}-${yearMonthDay[0]}`; // output yyyy-mm-dd format
    } else {
      this._value = `${yearMonthDay[2]}-${yearMonthDay[0]}-${yearMonthDay[1]}`; // output yyyy-mm-dd format
    }

    this.valueChange.emit(this.value);
    this.onChange(this._value);
  }

  /**
   * transform to basic format for server YYYY-MM-DD
   */
  public toFormat(date: Date) {
    const mm = date.getMonth() + 1; // getMonth() is zero-based
    const dd = date.getDate();
    return [date.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join('-');
  }

  private setDate(value: any) {
    if (!value) {
      this.date = null;
      return;
    }
    const date = new Date(value);
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const yy = date.getFullYear();

    if (this.isEUFormat) {
      this.date = `${dd}/${mm}/${yy}`;
    } else {
      this.date = `${mm}/${dd}/${yy}`;
    }
  }
}
